import { UsdtPrecision } from '../constants/currency';
import Num, { BigNumber, countDecimalPlaces, numberFormatter } from '../num';

const checkZeroValue = (val: string | number | undefined): val is undefined => {
  return !val || Number(val) === 0;
};

const getCurrentPercent = (val: string, max: string) => {
  const percent = Num(val).multipliedBy(100).dividedBy(max).toNumber();
  return Math.min(Math.round(percent), 100);
};

// amount(order size) = margin * leverage
// qty = margin * leverage / bestOfferEstimate
// https://github.prex.sh/Prex/prex-api-doc/blob/develop/Variables%20and%20Functions.md#order_position_sizesymbol
export const calcMarginToQty = (
  margin: string | undefined,
  leverage: number | undefined,
  bestOfferEstimate: string | undefined,
  tradeQtyPrecision: number | undefined,
) => {
  if (checkZeroValue(margin) || checkZeroValue(leverage) || checkZeroValue(bestOfferEstimate))
    return numberFormatter('0', { precision: tradeQtyPrecision, toFixed: true });
  const qty = Num(margin).multipliedBy(leverage).dividedBy(bestOfferEstimate);
  if (tradeQtyPrecision) return qty.toFixed(tradeQtyPrecision);
  return qty.toString();
};

export const calcMarginToAmount = (margin: string | undefined, leverage: number | undefined) => {
  if (checkZeroValue(margin) || checkZeroValue(leverage)) return '0.00';
  return Num(margin).multipliedBy(leverage).toFixed(UsdtPrecision);
};

export const calcQtyToMargin = (
  qty: string | undefined,
  leverage: number | undefined,
  bestOfferEstimate: string | undefined,
) => {
  if (checkZeroValue(qty) || checkZeroValue(leverage) || checkZeroValue(bestOfferEstimate)) return '0.00';
  return Num(qty).dividedBy(leverage).multipliedBy(bestOfferEstimate).toFixed(UsdtPrecision);
};

export const calcQtyToAmount = (qty: string | undefined, bestOfferEstimate: string | undefined) => {
  if (checkZeroValue(qty) || checkZeroValue(bestOfferEstimate)) return '0.00';
  return Num(qty).multipliedBy(bestOfferEstimate).toFixed(UsdtPrecision);
};

export const calcAmountToQty = (
  amount: string | undefined,
  bestOfferEstimate: string | undefined,
  tradeQtyPrecision?: number,
) => {
  if (checkZeroValue(amount) || checkZeroValue(bestOfferEstimate))
    return numberFormatter('0', { precision: tradeQtyPrecision, toFixed: true });
  const qty = Num(amount).dividedBy(bestOfferEstimate);
  if (tradeQtyPrecision) return qty.toFixed(tradeQtyPrecision);
  return qty.toString();
};

export const calcAmountToMargin = (amount: string | undefined, leverage: number | undefined) => {
  if (checkZeroValue(amount) || checkZeroValue(leverage)) return '0.00';
  return Num(amount).dividedBy(leverage).toFixed(UsdtPrecision);
};

export const calcAmountToLeverage = (amount: string | undefined, margin: string | undefined): number => {
  if (checkZeroValue(amount) || checkZeroValue(margin)) return 0;
  return Number(Num(amount).dividedBy(margin).toFixed(0));
};

export const calcInputMarginPercent = (
  inputMargin: string | undefined,
  availableFunds: string | undefined,
  leverage: number | undefined,
) => {
  if (checkZeroValue(inputMargin) || checkZeroValue(availableFunds) || checkZeroValue(leverage))
    return {
      amount: '0.00',
      percent: 0,
    };
  return {
    amount: calcMarginToAmount(inputMargin, leverage),
    percent: getCurrentPercent(inputMargin, availableFunds),
  };
};

export const calcInputQtyPercent = (
  inputQty: string | undefined,
  availableFunds: string | undefined,
  leverage: number | undefined,
  price: string,
  tradeQtyPrecision: number = 2,
) => {
  if (checkZeroValue(inputQty) || checkZeroValue(availableFunds) || checkZeroValue(leverage))
    return {
      amount: '0.00',
      percent: 0,
    };
  const maxQty = calcMarginToQty(availableFunds, leverage, price, tradeQtyPrecision);
  return {
    amount: calcQtyToAmount(inputQty, price),
    percent: getCurrentPercent(inputQty, maxQty),
  };
};

export const calcInputAmountPercent = (
  inputAmount: string | undefined,
  availableFunds: string | undefined,
  leverage: number | undefined,
  price: string,
  tradeQtyPrecision: number = 2,
) => {
  if (checkZeroValue(inputAmount) || checkZeroValue(availableFunds) || checkZeroValue(leverage))
    return {
      qty: numberFormatter('0', { precision: tradeQtyPrecision, toFixed: true }),
      percent: 0,
    };
  const maxAmount = calcMarginToAmount(availableFunds, leverage);
  return {
    qty: Num(inputAmount).dividedBy(price).toFixed(tradeQtyPrecision),
    percent: getCurrentPercent(inputAmount, maxAmount),
  };
};

export const calcPercentToMargin = (
  availableFunds: string | undefined,
  leverage: number | undefined,
  percent: number,
) => {
  if (checkZeroValue(availableFunds) || checkZeroValue(leverage))
    return {
      margin: '0.00',
      amount: '0.00',
    };
  const margin = Num(availableFunds).dividedBy(100).multipliedBy(percent).toFixed(UsdtPrecision);
  return {
    margin,
    amount: calcMarginToAmount(margin, leverage),
  };
};

export const calcPercentToAmount = (
  focusTarget: 'qty' | 'amount',
  availableFunds: string | undefined,
  leverage: number | undefined,
  price: string,
  percent: number,
  tradeQtyPrecision: number = 2,
) => {
  if (checkZeroValue(availableFunds) || checkZeroValue(leverage))
    return {
      qty: numberFormatter('0', { precision: tradeQtyPrecision, toFixed: true }),
      amount: '0.00',
    };
  if (focusTarget === 'qty') {
    const maxQty = Num(availableFunds).multipliedBy(leverage).dividedBy(price);
    const inputQty = maxQty.dividedBy(100).multipliedBy(percent);
    return {
      qty: inputQty.toFixed(tradeQtyPrecision),
      amount: inputQty.multipliedBy(price).toFixed(UsdtPrecision),
    };
  }
  const maxAmount = Num(availableFunds).multipliedBy(leverage);
  const inputAmount = maxAmount.dividedBy(100).multipliedBy(percent);
  return {
    qty: inputAmount.dividedBy(price).toFixed(tradeQtyPrecision),
    amount: inputAmount.toFixed(UsdtPrecision),
  };
};

export const calcSpotPercentToAmount = (
  focusTarget: 'quantity' | 'amount',
  sideType: 'Buy' | 'Sell',
  availableFunds: string | undefined,
  availableBaseCurrency: string | undefined,
  price: string,
  percent: number,
  tradeQtyPrecision: number = 2,
) => {
  if (checkZeroValue(availableFunds) || checkZeroValue(availableBaseCurrency))
    return {
      qty: numberFormatter('0', { precision: tradeQtyPrecision, toFixed: true }),
      amount: '0.00',
    };
  if (focusTarget === 'quantity') {
    const maxQty = Num(availableBaseCurrency);
    if (sideType === 'Buy') {
      // Trading Fee를 고려한 최대 주문 수량 계산
      const inputQty = maxQty.dividedBy(100).multipliedBy(percent);
      const inputAmount = inputQty.multipliedBy(price);

      return {
        qty: inputQty.toFixed(tradeQtyPrecision),
        amount: inputAmount.toFixed(UsdtPrecision),
      };
    }
    // Sell
    const inputQty = maxQty.dividedBy(100).multipliedBy(percent);
    return {
      qty: inputQty.toFixed(tradeQtyPrecision),
      amount: inputQty.multipliedBy(price).toFixed(UsdtPrecision),
    };
  } else {
    // focusTarget === 'amount'
    const maxAmount = Num(availableFunds);
    if (sideType === 'Buy') {
      // Trading Fee를 고려한 최대 주문 금액 계산
      const inputAmount = maxAmount.dividedBy(100).multipliedBy(percent);
      const inputQty = inputAmount.dividedBy(price);
      return {
        qty: inputQty.toFixed(tradeQtyPrecision),
        amount: inputAmount.toFixed(UsdtPrecision),
      };
    }
    // Sell
    const inputAmount = maxAmount.dividedBy(100).multipliedBy(percent);
    return {
      qty: inputAmount.dividedBy(price).toFixed(tradeQtyPrecision),
      amount: inputAmount.toFixed(UsdtPrecision),
    };
  }
};

// https://github.prex.sh/Prex/prex-api-doc/blob/develop/Variables%20and%20Functions.md#liq_price_estimatesymbol
// Client should display "-" if the value is negative

/**
 * @deprecated use calcEstLiquidationPriceWithFee
 */

export const calcEstLiquidationPrice = (
  side: 'Long' | 'Short',
  leverage?: number,
  bestOfferEstimate?: number | string,
  maintMarginRate?: string,
  tickSize?: string,
): string | undefined => {
  if (!leverage || !bestOfferEstimate || !maintMarginRate || !tickSize) return;
  const sideValue = side === 'Long' ? 1 : -1;
  const sideMethod = side === 'Long' ? Math.ceil : Math.floor;

  const liqPriceRaw = Num(bestOfferEstimate)
    .multipliedBy(1 - sideValue / leverage)
    .dividedBy(1 - sideValue * Number(maintMarginRate));
  const liqPriceEstimate = Num(sideMethod(liqPriceRaw.dividedBy(tickSize).toNumber())).multipliedBy(tickSize);

  if (liqPriceEstimate.isNegative()) return;
  return liqPriceEstimate.toFixed(countDecimalPlaces(tickSize));
};

// https://prextech.slack.com/archives/C087D884VEE/p1764303107237959?thread_ts=1764207713.217699&cid=C087D884VEE
// price * (1 - side * (1 / leverage - (max(takerFeeRate,makerFeeRate)))) / (1 - side * maintMarginRate)

export const calcEstLiquidationPriceWithFee = (
  side: 'Long' | 'Short',
  leverage: number,
  price: number | string,
  maintMarginRate: string,
  takerFeeRate: string,
  makerFeeRate: string,
  tickSize: string,
) => {
  const sideValue = side === 'Long' ? 1 : -1;
  const sideMethod = side === 'Long' ? Math.ceil : Math.floor;

  const entryFactor = Num(1).minus(
    Num(sideValue).multipliedBy(Num(1).dividedBy(Num(leverage)).minus(BigNumber.max(takerFeeRate, makerFeeRate))),
  );
  const marginFactor = Num(1).minus(Num(sideValue).multipliedBy(maintMarginRate));

  return Num(sideMethod(Num(price).multipliedBy(entryFactor).dividedBy(marginFactor).dividedBy(tickSize).toNumber()))
    .multipliedBy(tickSize)
    .toFixed(countDecimalPlaces(tickSize));
};
